import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import {
    FaQuestionCircle,
    FaReply,
    FaArrowLeft,
    FaCommentDots,
    FaChevronDown,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { showSuccessToast, showErrorToast } from "../utils/toastUtils";

const EntrepreneurQuestions = ({ user }) => {
    const navigate = useNavigate();
    const [campaignQuestions, setCampaignQuestions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(true);
    const [answerText, setAnswerText] = useState("");
    const [replyText, setReplyText] = useState("");
    const [selectedQuestionId, setSelectedQuestionId] = useState(null);
    const [showAnswerForm, setShowAnswerForm] = useState(false);
    // activeReplyKey: 'thread' = general reply at bottom, or a reply.id = reply-to-reply
    const [activeReplyKey, setActiveReplyKey] = useState(null);
    // expandedReplies: Set of reply IDs whose children are currently visible
    const [expandedReplies, setExpandedReplies] = useState(new Set());
    const [submittingAnswer, setSubmittingAnswer] = useState(false);
    const [submittingReply, setSubmittingReply] = useState(false);
    const [questionsFilter, setQuestionsFilter] = useState("all");

    // Fetch campaign questions for the entrepreneur
    useEffect(() => {
        const fetchCampaignQuestions = async () => {
            try {
                const userId = user?.id;

                if (!userId) return;

                setLoadingQuestions(true);
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/entrepreneur/${userId}/questions`;

                const res = await fetch(url, {
                    credentials: "include",
                });
                const data = await res.json();

                // If we have questions, fetch replies for each question
                if (Array.isArray(data) && data.length > 0) {
                    const questionsWithReplies = await Promise.all(
                        data.map(async (question) => {
                            try {
                                const repliesUrl = `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/questions/${question.id}/replies`;
                                const repliesRes = await fetch(repliesUrl, {
                                    credentials: "include",
                                });
                                const repliesData = await repliesRes.json();

                                // Add replies to the question object
                                return {
                                    ...question,
                                    replies: Array.isArray(repliesData)
                                        ? repliesData
                                        : [],
                                };
                            } catch (err) {
                                console.error(
                                    `Error fetching replies for question ${question.id}:`,
                                    err,
                                );
                                return {
                                    ...question,
                                    replies: [],
                                };
                            }
                        }),
                    );

                    setCampaignQuestions(questionsWithReplies);
                } else {
                    setCampaignQuestions(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                console.error("Error fetching campaign questions:", err);
                setCampaignQuestions([]);
            } finally {
                setLoadingQuestions(false);
            }
        };

        fetchCampaignQuestions();
    }, [user]);

    // Function to handle submitting an answer
    const handleAnswerSubmit = async (e) => {
        e.preventDefault();

        if (!answerText.trim() || !selectedQuestionId) return;
        if (!user?.id) {
            showErrorToast("You must be logged in to answer questions");
            return;
        }

        try {
            setSubmittingAnswer(true);
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/questions/${selectedQuestionId}/answer`;

            const response = await fetch(url, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    answer: answerText,
                    user_id: user?.id,
                }),
            });

            if (response.ok) {
                const updatedQuestion = await response.json();

                // Update the questions list with the new answer
                setCampaignQuestions((prevQuestions) =>
                    prevQuestions.map((q) =>
                        q.id === updatedQuestion.id ? updatedQuestion : q,
                    ),
                );

                // Reset form
                setAnswerText("");
                setSelectedQuestionId(null);
                setShowAnswerForm(false);

                // Show a success message with toast
                showSuccessToast(
                    "Your answer has been submitted successfully!",
                );
            } else {
                const errorData = await response.json();
                console.error("Error response:", errorData);
                showErrorToast(
                    `Error: ${
                        errorData.message ||
                        "Failed to submit answer. Please try again."
                    }`,
                );
            }
        } catch (err) {
            console.error("Error submitting answer:", err);
            showErrorToast(
                "There was an error submitting your answer. Please try again.",
            );
        } finally {
            setSubmittingAnswer(false);
        }
    };

    // Function to handle submitting a reply to a question or answer
    const handleReplySubmit = async (e) => {
        e.preventDefault();

        if (!replyText.trim() || !selectedQuestionId) return;
        if (!user?.id) {
            showErrorToast("You must be logged in to reply to questions");
            return;
        }

        try {
            setSubmittingReply(true);
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/questions/${selectedQuestionId}/replies`;

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    question: replyText,
                    user_id: user?.id,
                    reply_to_id:
                        activeReplyKey !== "thread" ? activeReplyKey : null,
                }),
            });

            if (response.ok) {
                const newReply = await response.json();

                // Update the questions list with the new reply
                setCampaignQuestions((prevQuestions) =>
                    prevQuestions.map((q) => {
                        if (q.id === selectedQuestionId) {
                            // Create replies array if it doesn't exist
                            const replies = Array.isArray(q.replies)
                                ? [...q.replies]
                                : [];
                            return {
                                ...q,
                                replies: [...replies, newReply],
                            };
                        }
                        return q;
                    }),
                );

                // Auto-expand the parent reply so the new reply is visible
                const repliedToKey = activeReplyKey;

                // Reset form
                setReplyText("");
                setSelectedQuestionId(null);
                setActiveReplyKey(null);

                if (repliedToKey && repliedToKey !== "thread") {
                    setExpandedReplies((prev) => {
                        const next = new Set(prev);
                        next.add(repliedToKey);
                        return next;
                    });
                }

                // Show a success message with toast
                showSuccessToast("Your reply has been submitted successfully!");
            } else {
                const errorData = await response.json();
                console.error("Error response:", errorData);
                showErrorToast(
                    `Error: ${
                        errorData.message ||
                        "Failed to submit reply. Please try again."
                    }`,
                );
            }
        } catch (err) {
            console.error("Error submitting reply:", err);
            showErrorToast(
                "There was an error submitting your reply. Please try again.",
            );
        } finally {
            setSubmittingReply(false);
        }
    };

    // Opens/toggles inline reply form per question thread or per specific reply
    const openReply = (questionId, key, mention) => {
        if (selectedQuestionId === questionId && activeReplyKey === key) {
            setSelectedQuestionId(null);
            setActiveReplyKey(null);
            setReplyText("");
        } else {
            setSelectedQuestionId(questionId);
            setActiveReplyKey(key);
            setReplyText(mention ? `@${mention} ` : "");
            setShowAnswerForm(false);
        }
    };

    const toggleEntrepReplyExpand = (replyId) => {
        setExpandedReplies((prev) => {
            const next = new Set(prev);
            if (next.has(replyId)) next.delete(replyId);
            else next.add(replyId);
            return next;
        });
    };

    // Recursively renders replies nested under who they responded to
    const renderEntrepRepliesTree = (
        allReplies,
        question,
        parentReplyId = null,
    ) => {
        const levelReplies = allReplies.filter((r) => {
            if (parentReplyId === null) {
                const pointsToAReply =
                    r.reply_to_id &&
                    allReplies.some((pr) => pr.id === r.reply_to_id);
                return !pointsToAReply;
            }
            return r.reply_to_id === parentReplyId;
        });

        return levelReplies.map((reply) => {
            const isReplyingToThis =
                selectedQuestionId === question.id &&
                activeReplyKey === reply.id;
            const replyFirstName = reply.asker_first_name || "Anonymous";
            const childReplies = allReplies.filter(
                (r) => r.reply_to_id === reply.id,
            );
            const isExpanded = expandedReplies.has(reply.id);

            return (
                <div
                    key={reply.id}
                    className={`p-3 rounded-md ${
                        reply.is_entrepreneur
                            ? "bg-[#C2D7E2]/20 border-l-4 border-[#C2D7E2]"
                            : "bg-gray-50 border-l-4 border-navy/70"
                    }`}
                >
                    <p className="text-gray-700">{reply.question}</p>
                    <div className="flex justify-between mt-2 items-center">
                        <p
                            className={`text-xs font-medium ${
                                reply.is_entrepreneur
                                    ? "text-navy"
                                    : "text-gray-600"
                            }`}
                        >
                            By:{" "}
                            {reply.asker_first_name
                                ? `${reply.asker_first_name} ${reply.asker_last_name}`
                                : reply.is_entrepreneur
                                  ? "Entrepreneur"
                                  : "Investor"}
                        </p>
                        <div className="flex items-center gap-3">
                            <p className="text-xs text-gray-500">
                                {new Date(
                                    reply.audit_created_on,
                                ).toLocaleString()}
                            </p>
                            <button
                                onClick={() =>
                                    openReply(
                                        question.id,
                                        reply.id,
                                        replyFirstName,
                                    )
                                }
                                className={`text-xs font-medium flex items-center gap-1 transition-colors ${
                                    isReplyingToThis
                                        ? "text-navy"
                                        : "text-gray-400 hover:text-navy"
                                }`}
                            >
                                <FaReply className="text-[10px]" />
                                {isReplyingToThis ? "Cancel" : "Reply"}
                            </button>
                        </div>
                    </div>
                    {/* Inline form for reply-to-this-reply */}
                    {isReplyingToThis && (
                        <form
                            onSubmit={handleReplySubmit}
                            className="mt-3 space-y-2 border-t border-gray-200 pt-3"
                        >
                            <textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-navy"
                                rows={2}
                                placeholder={`Replying to @${replyFirstName}…`}
                                autoFocus
                                required
                            />
                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() =>
                                        openReply(question.id, reply.id)
                                    }
                                    className="text-xs text-gray-500 px-2 py-1 rounded hover:bg-gray-100"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submittingReply}
                                    className="text-xs bg-navy text-white px-3 py-1 rounded flex items-center gap-1 disabled:opacity-60"
                                >
                                    {submittingReply ? (
                                        <>
                                            <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>{" "}
                                            Posting…
                                        </>
                                    ) : (
                                        <>
                                            <FaReply className="text-[10px]" />{" "}
                                            Reply
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                    {/* Nested children — collapsed by default, toggle to expand */}
                    {childReplies.length > 0 && (
                        <div className="mt-2">
                            <button
                                onClick={() =>
                                    toggleEntrepReplyExpand(reply.id)
                                }
                                className="flex items-center gap-1 text-xs text-navy font-medium hover:text-navy/70 transition-colors py-1"
                            >
                                <FaChevronDown
                                    className={`text-[9px] transition-transform ${isExpanded ? "rotate-180" : ""}`}
                                />
                                {isExpanded
                                    ? "Hide replies"
                                    : `${childReplies.length} repl${childReplies.length === 1 ? "y" : "ies"}`}
                            </button>
                            {isExpanded && (
                                <div className="ml-4 mt-2 pl-3 border-l-2 border-gray-200 space-y-3">
                                    {renderEntrepRepliesTree(
                                        allReplies,
                                        question,
                                        reply.id,
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        });
    };

    const filteredQuestions = campaignQuestions.filter(
        (q) => questionsFilter === "all" || !q.answer,
    );

    return (
        <div className="bg-white p-6 min-h-screen">
            <div className="max-w-6xl mx-auto">
                {/* Back button */}
                <button
                    onClick={() => navigate("/home")}
                    className="mb-4 text-navy hover:text-navy/80 flex items-center transition-colors duration-200"
                >
                    <FaArrowLeft className="mr-2" /> Back to Dashboard
                </button>

                {/* Header */}
                <div className="flex items-center justify-center mb-8 bg-navy p-4 rounded-lg shadow-md">
                    <h1 className="text-2xl font-bold text-white">
                        Campaign Questions & Answers
                    </h1>
                </div>

                {/* Filters */}
                <div className="mb-8 flex justify-between items-center bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <div className="text-navy">
                        <span className="font-medium">Total Questions:</span>{" "}
                        <span className="font-bold">
                            {campaignQuestions.length}
                        </span>
                        <span className="mx-2">•</span>
                        <span className="font-medium">Unanswered:</span>{" "}
                        <span className="font-bold text-amber-600">
                            {campaignQuestions.filter((q) => !q.answer).length}
                        </span>
                    </div>
                    <div className="flex">
                        <button
                            onClick={() => setQuestionsFilter("all")}
                            className={`px-4 py-2 rounded-l-md transition-colors duration-200 ${
                                questionsFilter === "all"
                                    ? "bg-navy text-white"
                                    : "bg-[#C2D7E2] text-navy hover:bg-[#C2D7E2]/80"
                            }`}
                        >
                            All Questions
                        </button>
                        <button
                            onClick={() => setQuestionsFilter("unanswered")}
                            className={`px-4 py-2 rounded-r-md transition-colors duration-200 ${
                                questionsFilter === "unanswered"
                                    ? "bg-navy text-white"
                                    : "bg-[#C2D7E2] text-navy hover:bg-[#C2D7E2]/80"
                            }`}
                        >
                            Unanswered
                        </button>
                    </div>
                </div>

                {/* Questions List */}
                {loadingQuestions ? (
                    <div className="text-center py-16 bg-white rounded-lg shadow-sm border border-gray-100">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
                        <p className="mt-4 text-navy font-medium">
                            Loading questions...
                        </p>
                    </div>
                ) : filteredQuestions.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-lg shadow-sm border border-gray-100">
                        <FaQuestionCircle className="mx-auto text-5xl text-[#C2D7E2] mb-4" />
                        <h3 className="text-xl font-semibold text-navy mb-3">
                            No Questions Found
                        </h3>
                        <p className="text-gray-600 max-w-md mx-auto">
                            {questionsFilter === "all"
                                ? "You don't have any questions for your campaigns yet."
                                : "You've answered all questions from your campaigns!"}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {filteredQuestions.map((question) => (
                            <div
                                key={question.id}
                                className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200"
                            >
                                <div className="flex justify-between mb-3">
                                    <span className="text-sm font-medium text-navy bg-[#C2D7E2]/30 px-3 py-1 rounded-full">
                                        Campaign: {question.campaign_name}
                                    </span>
                                    <span className="text-xs text-gray-500 flex items-center">
                                        <span className="inline-block w-2 h-2 rounded-full bg-gray-300 mr-2"></span>
                                        {new Date(
                                            question.audit_created_on,
                                        ).toLocaleDateString()}
                                    </span>
                                </div>

                                {/* Question content */}
                                <div className="mb-5">
                                    <h3 className="text-lg font-medium text-navy mb-2 flex items-center">
                                        <FaQuestionCircle className="mr-2 text-navy/70" />{" "}
                                        Question:
                                    </h3>
                                    <p className="text-gray-700 bg-gray-50 p-4 rounded-md border-l-4 border-navy">
                                        {question.question}
                                    </p>
                                </div>

                                {question.answer ? (
                                    <div className="mb-5">
                                        <h3 className="text-lg font-medium text-navy mb-2 flex items-center">
                                            <FaReply className="mr-2 text-navy/70" />{" "}
                                            Your Answer:
                                        </h3>
                                        <div className="bg-[#C2D7E2]/20 p-4 rounded-md border-l-4 border-[#C2D7E2]">
                                            <p className="text-gray-700">
                                                {question.answer}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-3 flex items-center">
                                                <span className="inline-block w-2 h-2 rounded-full bg-green-400 mr-2"></span>
                                                Answered on:{" "}
                                                {new Date(
                                                    question.audit_updated_on,
                                                ).toLocaleDateString()}
                                            </p>
                                        </div>

                                        {/* Reply button for answered questions */}
                                        <div className="flex justify-end mt-3">
                                            <button
                                                onClick={() =>
                                                    openReply(
                                                        question.id,
                                                        "thread",
                                                    )
                                                }
                                                className="px-3 py-1.5 bg-navy text-white rounded-md hover:bg-navy/90 flex items-center text-sm transition-colors duration-200"
                                            >
                                                <FaCommentDots className="mr-1.5" />{" "}
                                                {selectedQuestionId ===
                                                    question.id &&
                                                activeReplyKey === "thread"
                                                    ? "Cancel Reply"
                                                    : "Add Reply"}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex justify-end mb-5">
                                        <button
                                            onClick={() => {
                                                setSelectedQuestionId(
                                                    question.id,
                                                );
                                                setShowAnswerForm(true);
                                            }}
                                            className="px-4 py-2 bg-navy text-white rounded-md hover:bg-navy/90 flex items-center transition-colors duration-200"
                                        >
                                            <FaReply className="mr-2" /> Reply
                                            to Question
                                        </button>
                                    </div>
                                )}

                                {/* Display replies if any */}
                                {Array.isArray(question.replies) &&
                                    question.replies.length > 0 && (
                                        <div className="mt-5 border-t border-gray-200 pt-4">
                                            <h3 className="text-md font-medium text-navy mb-3 flex items-center">
                                                <FaCommentDots className="mr-2 text-navy/70" />{" "}
                                                Conversation Thread:
                                            </h3>
                                            <div className="space-y-3 ml-4 border-l-2 border-gray-200 pl-4">
                                                {renderEntrepRepliesTree(
                                                    question.replies,
                                                    question,
                                                )}
                                            </div>
                                        </div>
                                    )}

                                {/* Thread-level inline reply form */}
                                {selectedQuestionId === question.id &&
                                    activeReplyKey === "thread" && (
                                        <div className="mt-5 border-t border-gray-200 pt-4">
                                            <form
                                                onSubmit={handleReplySubmit}
                                                className="space-y-3"
                                            >
                                                <label className="block text-navy text-sm font-bold">
                                                    Your Reply:
                                                </label>
                                                <textarea
                                                    value={replyText}
                                                    onChange={(e) =>
                                                        setReplyText(
                                                            e.target.value,
                                                        )
                                                    }
                                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-navy"
                                                    rows={3}
                                                    placeholder="Type your reply here…"
                                                    autoFocus
                                                    required
                                                />
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            openReply(
                                                                question.id,
                                                                "thread",
                                                            )
                                                        }
                                                        className="text-sm text-gray-500 px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="submit"
                                                        disabled={
                                                            submittingReply
                                                        }
                                                        className="text-sm bg-navy text-white px-4 py-1.5 rounded flex items-center gap-1.5 disabled:opacity-60 hover:bg-navy/90"
                                                    >
                                                        {submittingReply ? (
                                                            <>
                                                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>{" "}
                                                                Submitting…
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaCommentDots />{" "}
                                                                Submit Reply
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Answer Question Form Modal */}
                {showAnswerForm && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
                        <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl">
                            <h3 className="text-xl font-semibold mb-4 flex items-center text-navy">
                                <FaQuestionCircle className="text-navy mr-2" />{" "}
                                Answer Question
                            </h3>

                            <div className="mb-5 bg-[#C2D7E2]/10 p-4 rounded-md border-l-4 border-navy">
                                <p className="text-navy/70 text-sm mb-1 font-medium">
                                    Question:
                                </p>
                                <p className="font-medium text-gray-700">
                                    {campaignQuestions.find(
                                        (q) => q.id === selectedQuestionId,
                                    )?.question || ""}
                                </p>
                                <p className="text-xs text-navy/60 mt-2 flex items-center">
                                    <span className="inline-block w-2 h-2 rounded-full bg-navy/40 mr-2"></span>
                                    From Campaign:{" "}
                                    {campaignQuestions.find(
                                        (q) => q.id === selectedQuestionId,
                                    )?.campaign_name || ""}
                                </p>
                            </div>

                            <form onSubmit={handleAnswerSubmit}>
                                <div className="mb-5">
                                    <label className="block text-navy text-sm font-bold mb-2">
                                        Your Answer:
                                    </label>
                                    <textarea
                                        value={answerText}
                                        onChange={(e) =>
                                            setAnswerText(e.target.value)
                                        }
                                        className="w-full px-4 py-3 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-navy focus:border-transparent"
                                        rows={6}
                                        placeholder="Type your answer here..."
                                        required
                                    />
                                </div>

                                <div className="flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowAnswerForm(false);
                                            setSelectedQuestionId(null);
                                            setAnswerText("");
                                        }}
                                        className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-5 py-2 bg-navy text-white rounded-md hover:bg-navy/90 flex items-center transition-colors duration-200"
                                        disabled={submittingAnswer}
                                    >
                                        {submittingAnswer ? (
                                            <>
                                                <svg
                                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    ></path>
                                                </svg>
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <FaReply className="mr-2" />{" "}
                                                Submit Answer
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

EntrepreneurQuestions.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
};
export default EntrepreneurQuestions;
